/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { LightSpec, TableInfoSpec } from "../../../shared/contracts";
import { LightStatus } from "../../core/enums";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

/**
 * The part of a light that the engine needs: the state, the blink pattern and
 * the fade. The renderer reads the colors and the outline from the spec.
 */
export class LightData extends ItemData {
  public center: Vertex2D;
  public state: number = LightStatus.LightStateOff;
  public rgBlinkPattern: string = "0";
  /** Time of one step of the blink pattern, in ms. */
  public blinkInterval: number = 125;
  public intensity: number = 1;
  public szSurface?: string;
  /** Fade speed in intensity units per ms. */
  public fadeSpeedUp: number = 0.2;
  public fadeSpeedDown: number = 0.2;
  public isVisible: boolean = true;

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: LightSpec, _info?: TableInfoSpec): LightData {
    const data = new LightData(spec.name, toVertex2D(spec.center));
    data.state = spec.state ?? data.state;
    data.rgBlinkPattern =
      spec.rgBlinkPattern && spec.rgBlinkPattern.length > 0 ? spec.rgBlinkPattern : "0";
    data.blinkInterval = spec.blinkInterval ?? data.blinkInterval;
    data.intensity = spec.intensity ?? data.intensity;
    data.szSurface = spec.szSurface;
    data.isVisible = spec.isVisible ?? true;
    return data;
  }

  public isOn(): boolean {
    if (this.state === LightStatus.LightStateOff) {
      return false;
    }
    if (this.state === LightStatus.LightStateBlinking) {
      return this.rgBlinkPattern[0] === "1";
    }
    return this.state === LightStatus.LightStateOn;
  }
}
